import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import DebugLogModal from '../components/DebugLogModal';
import { format } from 'date-fns';
import { ArrowLeft, RefreshCw, Eye } from 'lucide-react';

const AdminLogsPage = () => {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [selectedLog, setSelectedLog] = useState(null);

    const fetchLogs = React.useCallback(async () => {
        try {
            setLoading(true);
            const offset = (page - 1) * limit;
            const response = await fetch(`/api/admin/llm-logs?limit=${limit}&offset=${offset}`);
            if (!response.ok) throw new Error('Failed to fetch logs');
            const data = await response.json();
            setLogs(data.items);
            setTotal(data.total);
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    }, [page, limit]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleViewLog = (log) => {
        setSelectedLog(log);
    };

    const handleCloseModal = () => {
        setSelectedLog(null);
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/admin')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">LLM Interaction Logs</h1>
                </div>
                <button
                    onClick={fetchLogs}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 font-medium text-gray-500">Timestamp</th>
                                <th className="px-6 py-3 font-medium text-gray-500">Type</th>
                                <th className="px-6 py-3 font-medium text-gray-500">Model</th>
                                <th className="px-6 py-3 font-medium text-gray-500">Tokens</th>
                                <th className="px-6 py-3 font-medium text-gray-500">Latency</th>
                                <th className="px-6 py-3 font-medium text-gray-500 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        Loading logs...
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        No logs found.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 text-gray-900 whitespace-nowrap">
                                            {format(new Date(log.created_at), 'MMM d, HH:mm:ss')}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                                    ${log.interaction_type === 'merge' ? 'bg-blue-100 text-blue-800' :
                                                    log.interaction_type === 'revision' ? 'bg-purple-100 text-purple-800' :
                                                        log.interaction_type === 'cherry_pick' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-800'
                                                }`}
                                            >
                                                {log.interaction_type?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 font-mono text-xs">
                                            {log.llm_model}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {log.tokens_used || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {log.processing_time_ms ? `${log.processing_time_ms}ms` : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleViewLog(log)}
                                                className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                                            >
                                                <Eye size={16} />
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                        Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} results
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {selectedLog && (
                <DebugLogModal
                    log={selectedLog}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default AdminLogsPage;
